// src/pages/Blog.jsx
// Public Community blog (/community). Structure and behaviour come from the blog
// prototype; every colour, font and spacing value comes from styles/Blog.css, which is
// mapped to theme.js the same way About.css is. Nothing is styled with MUI sx here, for
// the reason given at the top of About.jsx — the stylesheet carries its own reset.
//
// One thing in the prototype is still deliberately absent: the "weekly tips" email
// capture bar, which waits for BLOG-5 and the sending infrastructure behind it.
//
// Likes and comments (BLOG-4) need a signed-in account, though reading never does. A
// signed-out visitor sees the counts and is sent to sign-in on their first click, rather
// than being shown a disabled control or an error after the fact.
//
// Filtering is server-side (see backend/routes/blog.py). The category counts and the tag
// row come from the response's `facets`, not from the loaded rows, because pagination
// means the client only ever holds one page — deriving them here would make the tab
// counts shuffle every time someone pressed "Load more".

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../utils/api';
import { isBlogAuthor } from '../utils/roles';
import '../styles/Blog.css';

// Label and icon per category. The colour lives in Blog.css as --cat-<key>, so a palette
// change is a stylesheet edit rather than a code one. Keys match the stored `category`.
const CATEGORIES = [
  { key: 'writing', label: 'Writing Tips', icon: '✍️' },
  { key: 'reading', label: 'Reading', icon: '\u{1F4D6}' },
  { key: 'collaboration', label: 'Collaboration', icon: '\u{1F91D}' },
  { key: 'bookknowhow', label: 'Book Know-How', icon: '\u{1F4DA}' },
  { key: 'publishing', label: 'Publishing', icon: '\u{1F680}' },
];

const CATEGORY_BY_KEY = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

const READ_TIMES = [
  { value: 'all', label: 'Any length' },
  { value: 'quick', label: 'Quick read (<5 min)' },
  { value: 'medium', label: 'Medium (5–10 min)' },
  { value: 'long', label: 'Long (10+ min)' },
];

const PAGE_SIZE = 12;

const EMPTY_POST = { title: '', category: 'writing', tags: '', excerpt: '', body: '' };

// A CTA card replaces no article — it is added after every sixth one, and never as the
// very last cell, where it would read as the end of the list rather than an aside.
const CTA_EVERY = 6;

function categoryMeta(key) {
  return CATEGORY_BY_KEY[key] || { key, label: key || 'Uncategorised', icon: '\u{1F4DD}' };
}

function initials(name) {
  return (name || '')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Blog() {
  const navigate = useNavigate();
  const { user, startCognito } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [navOpen, setNavOpen] = useState(false);

  // --- filter state ---
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');       // debounced copy, the one that fetches
  const [category, setCategory] = useState('all');
  const [activeTags, setActiveTags] = useState([]);
  const [readTime, setReadTime] = useState('all');
  const [sort, setSort] = useState('newest');

  // --- list state ---
  const [posts, setPosts] = useState([]);
  const [facets, setFacets] = useState({ categories: {}, tags: {} });
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listError, setListError] = useState('');

  // --- reader state ---
  const openSlug = searchParams.get('post');
  const [openPost, setOpenPost] = useState(null);
  const [readerLoading, setReaderLoading] = useState(false);
  const [readerError, setReaderError] = useState('');

  // --- weekly digest signup (BLOG-5) ---
  // The bar is hidden entirely unless the backend says email is configured. A
  // signup box that can only fail is worse than no signup box, and the endpoint
  // refuses anyway rather than banking addresses it cannot confirm.
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [leadEmail, setLeadEmail] = useState('');
  const [leadState, setLeadState] = useState('idle');   // idle | busy | done | error
  const [leadError, setLeadError] = useState('');
  const [subNotice, setSubNotice] = useState('');

  // --- compose, in a dialog on this page ---
  // Writing a post is a thing you do TO the blog you are looking at, so it happens here
  // rather than on a route of its own: publish and the new post is in the grid behind the
  // dialog, which is the confirmation. /blog-studio still exists for managing what you
  // have already written — a list, an edit, an unpublish — which is a different job.
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeForm, setComposeForm] = useState(EMPTY_POST);
  const [composeBusy, setComposeBusy] = useState(false);
  const [composeError, setComposeError] = useState('');
  const [composeNotice, setComposeNotice] = useState('');
  // Bumped after a publish so the list effect refetches and the new post appears.
  const [refreshKey, setRefreshKey] = useState(0);

  // --- comment thread, fetched alongside the open article ---
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentBusy, setCommentBusy] = useState(false);
  const [commentError, setCommentError] = useState('');

  const handleAuthCta = () => (user ? navigate('/dashboard') : startCognito('signup'));

  // Staff get a way in to the studio from the page they are looking at. Without this the
  // authoring surface was reachable only by typing /blog-studio.
  const mayAuthor = isBlogAuthor(user);

  useEffect(() => {
    fetch(`${API_BASE}/api/blog/subscription-status`)
      .then((res) => (res.ok ? res.json() : { enabled: false }))
      .then((data) => setEmailEnabled(Boolean(data.enabled)))
      .catch(() => setEmailEnabled(false));
  }, []);

  // Confirm and unsubscribe arrive as links in an email, so they land on this
  // page with a token rather than on routes of their own. The token is consumed
  // and stripped from the URL, so a refresh does not replay it.
  const confirmToken = searchParams.get('confirm');
  const unsubscribeToken = searchParams.get('unsubscribe');

  useEffect(() => {
    const token = confirmToken || unsubscribeToken;
    if (!token) return;
    const path = confirmToken ? 'confirm' : 'unsubscribe';

    fetch(`${API_BASE}/api/blog/subscribers/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          setSubNotice(data.error || 'That link is no longer valid.');
        } else if (confirmToken) {
          setSubNotice("You're subscribed. The digest goes out once a week.");
        } else {
          setSubNotice("You've been unsubscribed. No more emails.");
        }
      })
      .catch(() => setSubNotice('Something went wrong with that link.'))
      .finally(() => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('confirm');
          next.delete('unsubscribe');
          return next;
        }, { replace: true });
      });
    // Runs once per token. setSearchParams is stable, and re-running on it would
    // loop: the cleanup below changes the very params this reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmToken, unsubscribeToken]);

  const subscribe = async () => {
    const email = leadEmail.trim();
    if (!email || !email.includes('@')) {
      setLeadError('That does not look like an email address.');
      setLeadState('error');
      return;
    }
    setLeadState('busy');
    setLeadError('');
    try {
      const res = await fetch(`${API_BASE}/api/blog/subscribers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'That did not work.');
      }
      setLeadState('done');
      setLeadEmail('');
    } catch (e) {
      setLeadError(e.message);
      setLeadState('error');
    }
  };

  const navItems = [
    { label: 'Home', onClick: () => navigate('/') },
    { label: 'About', onClick: () => navigate('/about') },
    { label: 'Pricing', onClick: () => navigate('/pricing') },
    { label: 'Community', active: true, onClick: () => window.scrollTo({ top: 0, behavior: 'smooth' }) },
  ];

  // Debounce the search box so a fetch doesn't fire per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Every blog request carries the token when there is one, the public reads
  // included. They do not need it to succeed — but the list and detail routes
  // fill in viewerHasLiked from g.user, so a signed-in reader who omits it gets
  // hollow hearts on posts they have already liked. That is what happens when
  // only the write calls are authenticated.
  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('rv_access_token');
    return token
      ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      : { 'Content-Type': 'application/json' };
  }, []);

  const buildQuery = useCallback(
    (pageNum) => {
      const qs = new URLSearchParams();
      if (search) qs.set('search', search);
      if (category !== 'all') qs.set('category', category);
      activeTags.forEach((t) => qs.append('tag', t));
      if (readTime !== 'all') qs.set('readTime', readTime);
      qs.set('sort', sort);
      qs.set('page', String(pageNum));
      qs.set('limit', String(PAGE_SIZE));
      return qs.toString();
    },
    [search, category, activeTags, readTime, sort],
  );

  // Page 1 on any filter change. A stale in-flight response must not overwrite a newer
  // one, so each run marks itself cancelled on cleanup — typing fast otherwise lets an
  // earlier, slower query land last and show the wrong rows.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setListError('');

    fetch(`${API_BASE}/api/blog/posts?${buildQuery(1)}`, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((data) => {
        if (cancelled) return;
        setPosts(data.items || []);
        setFacets(data.facets || { categories: {}, tags: {} });
        setTotal(data.total || 0);
        setHasMore(Boolean(data.hasMore));
        setPage(1);
      })
      .catch(() => {
        if (!cancelled) setListError("We couldn't load the blog just now.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [buildQuery, authHeaders, refreshKey]);

  const submitPost = async (status) => {
    if (!composeForm.title.trim()) {
      setComposeError('A title is required.');
      return;
    }
    setComposeBusy(true);
    setComposeError('');
    try {
      const res = await fetch(`${API_BASE}/api/blog/posts`, {
        method: 'POST',
        credentials: 'omit',
        headers: authHeaders(),
        body: JSON.stringify({
          title: composeForm.title.trim(),
          category: composeForm.category,
          tags: composeForm.tags.split(',').map((t) => t.trim()).filter(Boolean),
          excerpt: composeForm.excerpt.trim(),
          body: composeForm.body,
          status,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      setComposeOpen(false);
      setComposeForm(EMPTY_POST);
      setComposeNotice(
        status === 'published'
          ? `"${data.title}" is live.`
          : `"${data.title}" was saved as a draft. It is in the studio until you publish it.`,
      );
      // Only a published post joins the public list; a draft would not appear, and
      // refetching anyway keeps the counts and facets honest.
      setRefreshKey((k) => k + 1);
    } catch (e) {
      setComposeError(e.message || "That didn't save.");
    } finally {
      setComposeBusy(false);
    }
  };

  const loadMore = () => {
    const next = page + 1;
    setLoadingMore(true);
    fetch(`${API_BASE}/api/blog/posts?${buildQuery(next)}`, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((data) => {
        setPosts((prev) => [...prev, ...(data.items || [])]);
        setHasMore(Boolean(data.hasMore));
        setPage(next);
      })
      .catch(() => setListError("We couldn't load more posts."))
      .finally(() => setLoadingMore(false));
  };

  // --- reader: driven by ?post=<slug> so a shared link opens the article ---
  useEffect(() => {
    if (!openSlug) {
      setOpenPost(null);
      setReaderError('');
      return undefined;
    }
    let cancelled = false;
    setReaderLoading(true);
    setReaderError('');
    setOpenPost(null);
    setComments([]);
    setCommentText('');
    setCommentError('');

    // The thread is public, and fetched in parallel rather than after the article —
    // it is below the fold, so waiting on it would delay text the reader wants first.
    fetch(`${API_BASE}/api/blog/posts/${encodeURIComponent(openSlug)}/comments`)
      .then((res) => (res.ok ? res.json() : { items: [] }))
      .then((data) => {
        if (!cancelled) setComments(data.items || []);
      })
      .catch(() => {});

    fetch(`${API_BASE}/api/blog/posts/${encodeURIComponent(openSlug)}`, {
      // A signed-in author may read their own unpublished draft at this URL, so the
      // token goes along when there is one. Signed-out readers still get published posts.
      headers: (() => {
        const token = localStorage.getItem('rv_access_token');
        return token ? { Authorization: `Bearer ${token}` } : {};
      })(),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((data) => {
        if (!cancelled) setOpenPost(data);
      })
      .catch(() => {
        if (!cancelled) setReaderError('That post is no longer available.');
      })
      .finally(() => {
        if (!cancelled) setReaderLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [openSlug]);

  // --- likes and comments ---
  // Without postLoginFrom, AuthContext's callback lands a reader who signed in to
  // like or comment on /home, and the post they were reading is gone. QuotePage
  // uses the same key to resume a parked quote.
  const loginReturningTo = (slug) => {
    sessionStorage.setItem('postLoginFrom', `/community?post=${encodeURIComponent(slug)}`);
    startCognito('login');
  };

  const toggleLike = async (post) => {
    if (!user) {
      loginReturningTo(post.slug);
      return;
    }
    // Optimistic, because a heart that waits for a round trip feels broken. The
    // response is authoritative and overwrites this a moment later.
    const optimistic = {
      viewerHasLiked: !post.viewerHasLiked,
      likesCount: post.likesCount + (post.viewerHasLiked ? -1 : 1),
    };
    setPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, ...optimistic } : p)));
    setOpenPost((prev) => (prev && prev.id === post.id ? { ...prev, ...optimistic } : prev));

    try {
      const res = await fetch(`${API_BASE}/api/blog/posts/${post.id}/like`, {
        method: 'POST', credentials: 'omit', headers: authHeaders(), body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const settled = { viewerHasLiked: data.liked, likesCount: data.likesCount };
      setPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, ...settled } : p)));
      setOpenPost((prev) => (prev && prev.id === post.id ? { ...prev, ...settled } : prev));
    } catch {
      // Put it back the way it was rather than leaving a heart that lies.
      const reverted = {
        viewerHasLiked: post.viewerHasLiked,
        likesCount: post.likesCount,
      };
      setPosts((prev) => prev.map((p) => (p.id === post.id ? { ...p, ...reverted } : p)));
      setOpenPost((prev) => (prev && prev.id === post.id ? { ...prev, ...reverted } : prev));
    }
  };

  const postComment = async () => {
    const text = commentText.trim();
    if (!text || !openPost) return;
    if (!user) {
      loginReturningTo(openPost.slug);
      return;
    }
    setCommentBusy(true);
    setCommentError('');
    try {
      const res = await fetch(`${API_BASE}/api/blog/posts/${openPost.id}/comments`, {
        method: 'POST', credentials: 'omit', headers: authHeaders(), body: JSON.stringify({ text }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setComments((prev) => [...prev, data]);
      setCommentText('');
      // The reader counts the thread it holds; the card behind it does not have
      // the thread, so its counter is nudged to match rather than left stale.
      setPosts((prev) => prev.map((p) => (
        p.id === openPost.id ? { ...p, commentsCount: (p.commentsCount || 0) + 1 } : p
      )));
    } catch (e) {
      setCommentError(e.message || "That comment didn't post.");
    } finally {
      setCommentBusy(false);
    }
  };

  const closeReader = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('post');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const openReader = (slug) => {
    const next = new URLSearchParams(searchParams);
    next.set('post', slug);
    setSearchParams(next);
  };

  // Escape closes the composer too, but never mid-save — losing a drafted post to a
  // stray keypress is not a recoverable mistake.
  useEffect(() => {
    if (!composeOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !composeBusy) setComposeOpen(false); };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [composeOpen, composeBusy]);

  // Escape closes, and the page behind must not scroll while the overlay is up.
  const closeRef = useRef(null);
  useEffect(() => {
    if (!openSlug) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeReader();
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [openSlug, closeReader, openPost]);

  const toggleTag = (tag) => {
    setActiveTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const filtersActive = category !== 'all' || activeTags.length > 0 || readTime !== 'all' || Boolean(search);

  const clearFilters = () => {
    setCategory('all');
    setActiveTags([]);
    setReadTime('all');
    setSearchInput('');
    setSearch('');
  };

  const tagEntries = Object.entries(facets.tags || {});

  return (
    <div className={user ? 'blog-page with-app-nav' : 'blog-page'}>
      <header className="masthead">
        <div className="glow g1" />
        <div className="glow g2" />

        <div className="wrap">
          {/* Signed in, App.js renders the app <Navbar /> above this page instead
              (APP_CHROME_WHEN_SIGNED_IN), so the marketing nav row would be a second
              header. The hero stays either way. */}
          {!user && (
          <nav>
            <Link to="/" className="site-logo" aria-label="ReVisionari home">
              <img src="/images/revisionari-logo.png" alt="ReVisionari" />
            </Link>

            <div className={`nav-links${navOpen ? ' open' : ''}`} id="blog-nav-links">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className={item.active ? 'active' : undefined}
                  aria-current={item.active ? 'page' : undefined}
                  onClick={() => {
                    setNavOpen(false);
                    item.onClick();
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <button type="button" className="nav-cta" onClick={handleAuthCta}>
              Login/Sign up
            </button>
            <button
              type="button"
              className={`nav-toggle${navOpen ? ' open' : ''}`}
              aria-expanded={navOpen}
              aria-controls="blog-nav-links"
              aria-label={navOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setNavOpen((open) => !open)}
            >
              <span />
              <span />
              <span />
            </button>
          </nav>
          )}

          <div className="hero">
            <span className="eyebrow">The Community blog</span>
            <h1>Ideas on reading, writing, and working together</h1>
            <p>
              Craft tips, collaboration know-how, and short reads on the parts of book-making
              nobody else explains clearly.
            </p>

            {mayAuthor && (
              <div className="hero-staff">
                <button
                  type="button"
                  className="staff-cta"
                  onClick={() => { setComposeError(''); setComposeOpen(true); }}
                >
                  <span aria-hidden="true">{'✎'}</span> Write a post
                </button>
                <button
                  type="button"
                  className="staff-link"
                  onClick={() => navigate('/blog-studio')}
                >
                  Manage posts
                </button>
              </div>
            )}

            {composeNotice && <p className="lead-success" role="status">{composeNotice}</p>}

            {emailEnabled && leadState !== 'done' && (
              <div className="lead-bar">
                <input
                  className="lead-input"
                  type="email"
                  value={leadEmail}
                  onChange={(e) => setLeadEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') subscribe(); }}
                  placeholder="you@email.com"
                  aria-label="Your email address"
                />
                <button
                  type="button"
                  className="lead-btn"
                  onClick={subscribe}
                  disabled={leadState === 'busy'}
                >
                  {leadState === 'busy' ? 'Sending…' : 'Get weekly posts'}
                </button>
              </div>
            )}

            {emailEnabled && leadState === 'done' && (
              <p className="lead-success">
                Check your inbox and click the link to confirm. Nothing is sent until you do.
              </p>
            )}

            {emailEnabled && leadState === 'error' && <p className="lead-error">{leadError}</p>}

            {emailEnabled && leadState === 'idle' && (
              <p className="lead-note">One email a week, only when something new is published.</p>
            )}

            {subNotice && <p className="lead-success" role="status">{subNotice}</p>}
          </div>
        </div>
      </header>

      {/* ============ FILTERS ============ */}
      <div className="filter-bar">
        <div className="wrap">
          <div className="filter-row">
            <input
              className="search-input"
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search articles&hellip;"
              aria-label="Search articles"
            />
            <select
              className="filter-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              aria-label="Sort articles"
            >
              <option value="newest">Sort: Newest</option>
              <option value="popular">Sort: Most liked</option>
              <option value="discussed">Sort: Most discussed</option>
            </select>
            <select
              className="filter-select"
              value={readTime}
              onChange={(e) => setReadTime(e.target.value)}
              aria-label="Filter by length"
            >
              {READ_TIMES.map((rt) => (
                <option key={rt.value} value={rt.value}>{rt.label}</option>
              ))}
            </select>
          </div>

          <div className="cat-tabs">
            <button
              type="button"
              className={`cat-tab${category === 'all' ? ' active' : ''}`}
              aria-pressed={category === 'all'}
              onClick={() => setCategory('all')}
            >
              All articles
              <span className="ct-count">
                ({Object.values(facets.categories || {}).reduce((a, b) => a + b, 0)})
              </span>
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                type="button"
                className={`cat-tab${category === c.key ? ' active' : ''}`}
                aria-pressed={category === c.key}
                onClick={() => setCategory(category === c.key ? 'all' : c.key)}
              >
                <span className="ct-dot" style={{ background: `var(--cat-${c.key})` }} />
                {c.label}
                <span className="ct-count">({facets.categories?.[c.key] || 0})</span>
              </button>
            ))}
          </div>

          {tagEntries.length > 0 && (
            <div className="tag-row">
              {tagEntries.map(([tag, count]) => (
                <button
                  key={tag}
                  type="button"
                  className={`tag-chip${activeTags.includes(tag) ? ' active' : ''}`}
                  aria-pressed={activeTags.includes(tag)}
                  onClick={() => toggleTag(tag)}
                >
                  #{tag} ({count})
                </button>
              ))}
            </div>
          )}

          <div className="filter-count" aria-live="polite">
            {loading ? 'Loading…' : `${total} article${total === 1 ? '' : 's'}`}
            {filtersActive && !loading && (
              <>
                {' · '}
                <button type="button" onClick={clearFilters}>clear filters</button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ============ GRID ============ */}
      <section className="grid-section">
        <div className="wrap">
          <div className="article-grid">
            {loading &&
              Array.from({ length: 6 }).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className="skeleton-card" key={`sk-${i}`} aria-hidden="true">
                  <div className="sk-cover" />
                  <div className="sk-line" />
                  <div className="sk-line" />
                  <div className="sk-line short" />
                </div>
              ))}

            {!loading && listError && (
              <div className="grid-note">
                <div className="gn-icon">{'⚠️'}</div>
                <p>{listError}</p>
              </div>
            )}

            {!loading && !listError && posts.length === 0 && (
              <div className="grid-note">
                <div className="gn-icon">{'\u{1F4DC}'}</div>
                <p>
                  {filtersActive ? (
                    <>
                      No articles match these filters.{' '}
                      <button type="button" onClick={clearFilters}>Clear filters</button>
                    </>
                  ) : (
                    'No articles have been published yet. Check back soon.'
                  )}
                </p>
              </div>
            )}

            {!loading &&
              !listError &&
              posts.map((post, i) => {
                const meta = categoryMeta(post.category);
                const cards = [
                  <button
                    type="button"
                    className="article-card"
                    key={post.id || post.slug}
                    onClick={() => openReader(post.slug)}
                  >
                    <div className="article-cover" style={{ background: `var(--cat-${meta.key}-bg)` }}>
                      <span className="ac-icon" aria-hidden="true">{meta.icon}</span>
                      <span className="ac-cattag" style={{ color: `var(--cat-${meta.key})` }}>
                        {meta.label}
                      </span>
                    </div>
                    <div className="article-body">
                      <div className="article-title">{post.title}</div>
                      <div className="article-excerpt">{post.excerpt}</div>
                      <div className="article-meta">
                        <span className="article-avatar" aria-hidden="true">
                          {initials(post.authorName)}
                        </span>
                        <span>{post.authorName}</span>
                        <span aria-hidden="true">&middot;</span>
                        <span>{formatDate(post.publishedAt)}</span>
                        <span aria-hidden="true">&middot;</span>
                        <span>{post.readTimeMinutes} min read</span>
                      </div>
                      {post.tags?.length > 0 && (
                        <div className="article-tags">
                          {post.tags.map((t) => <span key={t}>#{t}</span>)}
                        </div>
                      )}

                      {/* The card is itself a button, so these cannot be nested
                          buttons. A span with a role and a key handler keeps the
                          heart operable without invalid markup. */}
                      <div className="engage-row">
                        <span
                          role="button"
                          tabIndex={0}
                          className={`engage-btn${post.viewerHasLiked ? ' liked' : ''}`}
                          aria-label={post.viewerHasLiked ? 'Unlike this article' : 'Like this article'}
                          aria-pressed={Boolean(post.viewerHasLiked)}
                          onClick={(e) => { e.stopPropagation(); toggleLike(post); }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleLike(post);
                            }
                          }}
                        >
                          <span className="eb-icon" aria-hidden="true">
                            {post.viewerHasLiked ? '♥' : '♡'}
                          </span>
                          {post.likesCount}
                        </span>
                        <span className="engage-btn" aria-label={`${post.commentsCount} comments`}>
                          <span className="eb-icon" aria-hidden="true">{'\u{1F4AC}'}</span>
                          {post.commentsCount}
                        </span>
                      </div>
                    </div>
                  </button>,
                ];

                if ((i + 1) % CTA_EVERY === 0 && i !== posts.length - 1) {
                  cards.push(
                    <div className="cta-card" key={`cta-${post.id || post.slug}`}>
                      <div className="cta-glow" />
                      <h3>Ready to write the book?</h3>
                      <p>
                        Pick a template, start drafting, and bring in collaborators, all in one
                        workspace.
                      </p>
                      <button type="button" onClick={handleAuthCta}>Start writing &rarr;</button>
                    </div>,
                  );
                }
                return cards;
              })}
          </div>

          {!loading && !listError && hasMore && (
            <button type="button" className="load-more" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? 'Loading…' : 'Load more articles'}
            </button>
          )}
        </div>
      </section>

      {/* ============ COMPOSE ============ */}
      {composeOpen && (
        <div
          className="compose-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Write a post"
          onClick={(e) => { if (e.target === e.currentTarget && !composeBusy) setComposeOpen(false); }}
        >
          <div className="compose-box">
            <div className="compose-head">
              <h2>Write a post</h2>
              <button
                type="button"
                className="compose-close"
                aria-label="Close"
                onClick={() => setComposeOpen(false)}
                disabled={composeBusy}
              >
                &times;
              </button>
            </div>

            <div className="compose-body">
              <label className="compose-field">
                <span>Title</span>
                <input
                  value={composeForm.title}
                  onChange={(e) => setComposeForm({ ...composeForm, title: e.target.value })}
                  maxLength={200}
                  autoFocus
                />
              </label>

              <div className="compose-row">
                <label className="compose-field">
                  <span>Category</span>
                  <select
                    value={composeForm.category}
                    onChange={(e) => setComposeForm({ ...composeForm, category: e.target.value })}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                </label>
                <label className="compose-field">
                  <span>Tags</span>
                  <input
                    value={composeForm.tags}
                    onChange={(e) => setComposeForm({ ...composeForm, tags: e.target.value })}
                    placeholder="firstdraft, querying"
                  />
                  <small>Comma separated.</small>
                </label>
              </div>

              <label className="compose-field">
                <span>Excerpt</span>
                <input
                  value={composeForm.excerpt}
                  onChange={(e) => setComposeForm({ ...composeForm, excerpt: e.target.value })}
                  maxLength={400}
                />
                <small>Shown on the card. Leave blank to use the first paragraph.</small>
              </label>

              <label className="compose-field">
                <span>Body</span>
                <textarea
                  rows={12}
                  value={composeForm.body}
                  onChange={(e) => setComposeForm({ ...composeForm, body: e.target.value })}
                />
                <small>Separate paragraphs with a blank line. Reading time comes from this.</small>
              </label>

              {composeError && <p className="compose-error">{composeError}</p>}
            </div>

            <div className="compose-actions">
              <button
                type="button"
                className="compose-secondary"
                onClick={() => submitPost('draft')}
                disabled={composeBusy}
              >
                Save draft
              </button>
              <button
                type="button"
                className="compose-primary"
                onClick={() => submitPost('published')}
                disabled={composeBusy}
              >
                {composeBusy ? 'Saving…' : 'Publish'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============ READER ============ */}
      {openSlug && (
        <div
          className="reader-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={openPost?.title || 'Article'}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeReader();
          }}
        >
          <div className="reader-box">
            {readerLoading && <div className="reader-loading">Loading article&hellip;</div>}

            {!readerLoading && readerError && (
              <div className="reader-error">
                <p>{readerError}</p>
                <button type="button" className="load-more" onClick={closeReader}>
                  Back to the blog
                </button>
              </div>
            )}

            {!readerLoading && openPost && (
              <>
                <div
                  className="reader-cover"
                  style={{ background: `var(--cat-${categoryMeta(openPost.category).key}-bg)` }}
                >
                  <button
                    type="button"
                    className="reader-close"
                    onClick={closeReader}
                    aria-label="Close article"
                    ref={closeRef}
                  >
                    &times;
                  </button>
                  <span className="rc-icon" aria-hidden="true">
                    {categoryMeta(openPost.category).icon}
                  </span>
                </div>

                <div className="reader-content">
                  <span
                    className="reader-cat"
                    style={{ color: `var(--cat-${categoryMeta(openPost.category).key})` }}
                  >
                    {categoryMeta(openPost.category).label}
                  </span>
                  <h1 className="reader-title">{openPost.title}</h1>

                  <div className="reader-meta">
                    <span className="reader-avatar" aria-hidden="true">
                      {initials(openPost.authorName)}
                    </span>
                    <span>{openPost.authorName}</span>
                    <span aria-hidden="true">&middot;</span>
                    <span>{formatDate(openPost.publishedAt)}</span>
                    <span aria-hidden="true">&middot;</span>
                    <span>{openPost.readTimeMinutes} min read</span>
                    {/* Only an author ever receives their own unpublished post, so this
                        badge is the signal that they are looking at a preview. */}
                    {openPost.status === 'draft' && (
                      <span className="reader-draft-badge">Draft preview</span>
                    )}
                  </div>

                  <div className="reader-body">
                    {(openPost.body || '')
                      .split(/\n{2,}/)
                      .filter((p) => p.trim())
                      // Index key: two identical paragraphs in one article are rare but
                      // legal, and the list never reorders.
                      // eslint-disable-next-line react/no-array-index-key
                      .map((para, i) => <p key={i}>{para}</p>)}
                  </div>

                  {openPost.tags?.length > 0 && (
                    <div className="reader-tags">
                      {openPost.tags.map((t) => <span key={t}>#{t}</span>)}
                    </div>
                  )}

                  <div className="reader-engage">
                    <button
                      type="button"
                      className={`reader-engage-btn${openPost.viewerHasLiked ? ' liked' : ''}`}
                      aria-pressed={Boolean(openPost.viewerHasLiked)}
                      onClick={() => toggleLike(openPost)}
                    >
                      <span className="eb-icon" aria-hidden="true">
                        {openPost.viewerHasLiked ? '♥' : '♡'}
                      </span>
                      {openPost.likesCount} {openPost.likesCount === 1 ? 'like' : 'likes'}
                    </button>
                    {/* comments.length, not openPost.commentsCount: the thread itself is
                        loaded here, so counting it can never disagree with what is
                        rendered below. The denormalised counter is for the cards, where
                        the thread is not fetched. */}
                    <span className="reader-engage-btn" aria-hidden="true">
                      <span className="eb-icon">{'\u{1F4AC}'}</span>
                      {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
                    </span>
                  </div>

                  <div className="reader-cta">
                    <p>Enjoyed this? ReVisionari helps you draft, revise and publish.</p>
                    <button type="button" onClick={handleAuthCta}>
                      Start your project &rarr;
                    </button>
                  </div>

                  <div className="comments-section">
                    <div className="comments-label">Comments</div>

                    {comments.length === 0 && (
                      <p className="no-comments-note">Be the first to comment.</p>
                    )}

                    {comments.map((c) => (
                      <div className="comment-item" key={c.id}>
                        <span className="comment-avatar" aria-hidden="true">
                          {initials(c.authorName)}
                        </span>
                        <div className="comment-bubble">
                          <div className="comment-head">
                            <span className="comment-name">{c.authorName}</span>
                            <span className="comment-time">{formatDate(c.createdAt)}</span>
                          </div>
                          <div className="comment-text">{c.text}</div>
                        </div>
                      </div>
                    ))}

                    {commentError && <p className="comment-error">{commentError}</p>}

                    {/* No name field, unlike the prototype: the account supplies the
                        name, so there is nothing here to post under someone else's. */}
                    {user ? (
                      <div className="comment-form">
                        <input
                          className="comment-text-input"
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') postComment(); }}
                          placeholder="Add a comment&hellip;"
                          aria-label="Add a comment"
                          maxLength={2000}
                        />
                        <button
                          type="button"
                          className="comment-submit"
                          onClick={postComment}
                          disabled={commentBusy || !commentText.trim()}
                        >
                          {commentBusy ? 'Posting…' : 'Post'}
                        </button>
                      </div>
                    ) : (
                      <p className="comment-signin">
                        <button type="button" onClick={() => loginReturningTo(openSlug)}>
                          Sign in
                        </button>
                        {' to join the conversation.'}
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
